// components/RestaurantLinktree.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    Switch,
    CircularProgress,
    Divider,
    Alert,
    Snackbar,
    Stack,
    Tooltip,
    Menu,
    MenuItem,
    Popover,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    PhotoCamera as PhotoCameraIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    ContentCopy as ContentCopyIcon,
    Link as LinkIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    YouTube as YouTubeIcon,
    WhatsApp as WhatsAppIcon,
    Telegram as TelegramIcon,
    MusicNote as MusicNoteIcon,
    Lock as LockIcon,
    Pinterest as PinterestIcon,
    LinkedIn as LinkedInIcon,
    Reddit as RedditIcon,
    GitHub as GitHubIcon,
    Language as LanguageIcon,
    Moped as MopedIcon,
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import { makeRequest } from '../../../assets/functions_helper';
import { SendButton } from '../../../components/buttons';
import BackgroundImageSelectionDialog from '../restaurants/image_components/background_image_selection_dialog';

// Opciones de iconos predefinidas
const iconOptions = [
    { value: 'link', label: 'Enlace', icon: LinkIcon },
    { value: 'facebook', label: 'Facebook', icon: FacebookIcon },
    { value: 'instagram', label: 'Instagram', icon: InstagramIcon },
    { value: 'twitter', label: 'Twitter', icon: TwitterIcon },
    { value: 'youtube', label: 'YouTube', icon: YouTubeIcon },
    { value: 'whatsapp', label: 'WhatsApp', icon: WhatsAppIcon },
    { value: 'telegram', label: 'Telegram', icon: TelegramIcon },
    { value: 'tiktok', label: 'TikTok', icon: MusicNoteIcon },
    { value: 'pinterest', label: 'Pinterest', icon: PinterestIcon },
    { value: 'linkedin', label: 'LinkedIn', icon: LinkedInIcon },
    { value: 'reddit', label: 'Reddit', icon: RedditIcon },
    { value: 'github', label: 'GitHub', icon: GitHubIcon },
    { value: 'onlyfans', label: 'OnlyFans (18+)', icon: LockIcon },
    { value: 'web', label: 'Sitio web', icon: LanguageIcon },
    { value: 'delivery', label: 'Domicilio', icon: MopedIcon },
];

// Generador de ID aleatorio de 32 caracteres
const generateRandomId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < 32; i++) {
        id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
};

// Función para obtener color de texto contrastante (blanco para fondos oscuros, negro para claros)
const getContrastText = (hexcolor) => {
    if (!hexcolor) return '#1E1E1E';
    const color = hexcolor.startsWith('#') ? hexcolor.slice(1) : hexcolor;
    if (color.length === 3) {
        const r = parseInt(color[0] + color[0], 16);
        const g = parseInt(color[1] + color[1], 16);
        const b = parseInt(color[2] + color[2], 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#1E1E1E' : '#FFFFFF';
    } else if (color.length === 6) {
        const r = parseInt(color.slice(0, 2), 16);
        const g = parseInt(color.slice(2, 4), 16);
        const b = parseInt(color.slice(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#1E1E1E' : '#FFFFFF';
    }
    return '#1E1E1E';
};

// Hook personalizado para manejar las llamadas a la API
const useLinktreeApi = (restaurantId) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    const fetchLinktree = useCallback(async () => {
        setLoading(true);
        try {
            const response = await makeRequest(
                'GET',
                `restaurant_admin/link_page/${restaurantId}`,
                null,
                'application/json',
                localStorage.getItem('token'),
                localStorage.getItem('tokenType')
            );

            if (response && response.detail && Array.isArray(response.detail) && response.detail.length === 0) {
                setData(null);
            } else {
                setData(response);
            }
        } catch (err) {
            if (err.status === 404) {
                setData(null);
            } else {
                console.error('Error al cargar la información', err);
            }
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    const createLinktree = useCallback(async (payload) => {
        setLoading(true);
        try {
            const response = await makeRequest(
                'POST',
                `restaurant_admin/link_page/${restaurantId}`,
                payload,
                'application/json',
                localStorage.getItem('token'),
                localStorage.getItem('tokenType')
            );
            setData(response);
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    const updateLinktree = useCallback(async (payload) => {
        setLoading(true);
        try {
            const response = await makeRequest(
                'PUT',
                `restaurant_admin/link_page/${restaurantId}`,
                payload,
                'application/json',
                localStorage.getItem('token'),
                localStorage.getItem('tokenType')
            );
            setData(response);
            return response;
        } catch (err) {
            throw err;
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    return { loading, data, fetchLinktree, createLinktree, updateLinktree };
};

export default function RestaurantLinktree({ restaurant }) {
    const restaurantId = restaurant?.id;
    const { loading, data, fetchLinktree, createLinktree, updateLinktree } = useLinktreeApi(restaurantId);

    // Estado del formulario
    const [form, setForm] = useState({
        background_image_url: '',
        background_image_id: '',
        logo_url: '',
        logo_id: '',
        links: [], // cada link: { id, title, url, is_active, icon, button_color }
    });

    const baseUrl = window.location.origin;
    const [deliveryEnabled, setDeliveryEnabled] = useState(false);
    const [isFetchingDeliveryUrl, setIsFetchingDeliveryUrl] = useState(false);
    const [whatsappAlertOpen, setWhatsappAlertOpen] = useState(false);

    const [imageSelectionOpen, setImageSelectionOpen] = useState(false);
    const [imageSelectionType, setImageSelectionType] = useState(null); // 'bg' o 'logo'
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Estado para el menú de iconos
    const [iconMenuAnchor, setIconMenuAnchor] = useState(null);
    const [iconMenuLinkId, setIconMenuLinkId] = useState(null);

    // Estado para el selector de color (popover)
    const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
    const [colorPickerLinkId, setColorPickerLinkId] = useState(null);

    // Cargar datos al montar
    useEffect(() => {
        if (restaurantId) {
            fetchLinktree();
        }
    }, [restaurantId, fetchLinktree]);

    // Poblar formulario con datos, asignando IDs únicos y color por defecto si no existe
    useEffect(() => {
        if (data) {
            setForm({
                background_image_url: data.background_image_url || '',
                background_image_id: data.background_image_id || '',
                logo_url: data.logo_url || '',
                logo_id: data.logo_id || '',
                links: (data.links || []).map(link => ({
                    ...link,
                    id: link.id || generateRandomId(),
                    // Si no viene button_color, usar dark_mode para definir color (mantener compatibilidad)
                    button_color: link.button_color || (link.dark_mode ? '#1E1E1E' : '#FFFFFF'),
                })),
            });
            
            // Verificar si el switch de domicilio debe estar encendido (si existe un enlace de delivery activo)
            const hasDelivery = (data.links || []).some(link => (link.icon === 'delivery' || link.url.includes('/delivery/')) && link.is_active);
            setDeliveryEnabled(hasDelivery);
        } else {
            setForm({
                background_image_url: '',
                background_image_id: '',
                logo_url: '',
                logo_id: '',
                links: [],
            });
            setDeliveryEnabled(false);
        }
    }, [data]);

    // Gestión de Domicilios
    const handleToggleDelivery = async (checked) => {
        setDeliveryEnabled(checked);
        if (checked) {
            setIsFetchingDeliveryUrl(true);
            try {
                // Consumir API para obtener el public_id y el whatsapp al mismo tiempo
                const infoPromise = makeRequest(
                    'GET',
                    `restaurants/restaurant/info?restaurant_id=${restaurantId}`,
                    null,
                    'application/json',
                    localStorage.getItem('token'),
                    localStorage.getItem('tokenType')
                );

                const waPromise = makeRequest(
                    'GET',
                    `restaurant_admin/public/whatsapp_phone/${restaurantId}`,
                    null,
                    'application/json'
                );

                const [infoResult, waResult] = await Promise.allSettled([infoPromise, waPromise]);

                if (infoResult.status === "rejected") {
                    throw infoResult.reason;
                }

                const response = infoResult.value;
                const publicId = response.public_id || restaurantId;

                // VALIDACIÓN: Verificar si hay un teléfono de WhatsApp configurado
                if (waResult.status === "rejected" || !waResult.value || !waResult.value.whatsapp_phone) {
                    setWhatsappAlertOpen(true);
                    setDeliveryEnabled(false);
                    setIsFetchingDeliveryUrl(false);
                    return;
                }

                const activeDeliveryUrl = `${window.location.origin}/delivery/${publicId}`;

                // Verificar si ya existía el enlace (por URL o icono)
                const exists = form.links.find(link => link.icon === 'delivery' || link.url.includes('/delivery/'));
                if (exists) {
                    // Actualizar tanto el estado is_active como reescribir la URL por seguridad
                    setForm(prev => {
                        const updatedLinks = [...prev.links];
                        const index = updatedLinks.findIndex(l => l.id === exists.id);
                        updatedLinks[index] = { ...updatedLinks[index], is_active: true, url: activeDeliveryUrl };
                        return { ...prev, links: updatedLinks };
                    });
                } else {
                    const newLink = {
                        id: generateRandomId(),
                        title: 'Pedir a Domicilio',
                        url: activeDeliveryUrl,
                        is_active: true,
                        icon: 'delivery',
                        button_color: '#F72E41',
                    };
                    setForm(prev => ({ ...prev, links: [newLink, ...prev.links] }));
                }
            } catch (error) {
                console.error("Error al obtener info del restaurante para domicilios", error);
                setDeliveryEnabled(false); // Revertir en caso de fallo
                setSnackbar({ open: true, message: 'Error al activar Domicilios', severity: 'error' });
            } finally {
                setIsFetchingDeliveryUrl(false);
            }
        } else {
            // Desactiva el enlace de domicilios ocultándolo del public linktree
            const exists = form.links.find(link => link.icon === 'delivery' || link.url.includes('/delivery/'));
            if (exists) {
                updateLink(exists.id, 'is_active', false);
            }
        }
    };

    // Gestión de enlaces
    const addLink = () => {
        const newLink = {
            id: generateRandomId(),
            title: '',
            url: '',
            is_active: true,
            icon: 'link',
            button_color: '#FFFFFF', // Color por defecto: blanco
        };
        setForm((prev) => ({
            ...prev,
            links: [...prev.links, newLink],
        }));
    };

    const updateLink = (id, field, value) => {
        setForm((prev) => ({
            ...prev,
            links: prev.links.map((link) =>
                link.id === id ? { ...link, [field]: value } : link
            ),
        }));
    };

    const removeLink = (id) => {
        setForm((prev) => ({
            ...prev,
            links: prev.links.filter((link) => link.id !== id),
        }));
    };

    // Funciones para mover enlaces (reordenar)
    const moveLinkUp = (index) => {
        if (index === 0) return;
        const newLinks = [...form.links];
        [newLinks[index - 1], newLinks[index]] = [newLinks[index], newLinks[index - 1]];
        setForm((prev) => ({ ...prev, links: newLinks }));
    };

    const moveLinkDown = (index) => {
        if (index === form.links.length - 1) return;
        const newLinks = [...form.links];
        [newLinks[index], newLinks[index + 1]] = [newLinks[index + 1], newLinks[index]];
        setForm((prev) => ({ ...prev, links: newLinks }));
    };

    // Selección de imágenes
    const openImageSelection = (type) => {
        setImageSelectionType(type);
        setImageSelectionOpen(true);
    };

    const handleImageSelected = (image) => {
        if (imageSelectionType === 'bg') {
            setForm((prev) => ({
                ...prev,
                background_image_url: image.image_url,
                background_image_id: image.id,
            }));
        } else if (imageSelectionType === 'logo') {
            setForm((prev) => ({
                ...prev,
                logo_url: image.image_url,
                logo_id: image.id,
            }));
        }
        setImageSelectionOpen(false);
        setImageSelectionType(null);
    };

    const handleCloseImageSelection = () => {
        setImageSelectionOpen(false);
        setImageSelectionType(null);
    };

    // Guardar y luego recargar datos
    const handleSave = async () => {
        try {
            const payload = {
                background_image_url: form.background_image_url,
                background_image_id: form.background_image_id,
                logo_url: form.logo_url,
                logo_id: form.logo_id,
                links: form.links.map(({ id, ...rest }) => rest), // se envía también button_color
            };

            if (data) {
                await updateLinktree(payload);
            } else {
                await createLinktree(payload);
            }

            await fetchLinktree();

            setSnackbar({ open: true, message: 'Linktree guardado con éxito', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
        }
    };

    // Copiar URL pública al portapapeles
    const handleCopyLink = () => {
        if (data?.public_id) {
            const url = `${baseUrl}/linktree/${data.public_id}`;
            navigator.clipboard.writeText(url);
            setSnackbar({ open: true, message: 'URL copiada al portapapeles', severity: 'success' });
        }
    };

    // Manejadores para el popover de color
    const openColorPicker = (event, linkId) => {
        setColorPickerAnchor(event.currentTarget);
        setColorPickerLinkId(linkId);
    };

    const closeColorPicker = () => {
        setColorPickerAnchor(null);
        setColorPickerLinkId(null);
    };

    if (loading && !form.links.length) {
        return null;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={4}>
                {/* Columna izquierda: Formulario */}
                <Grid item xs={12} md={6}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: '24px',
                            border: '1px solid #E0E0E0',
                            boxShadow: '0 8px 20px rgba(0,0,0,0.02)',
                        }}
                    >
                        <Typography variant="h6" gutterBottom sx={{ fontFamily: 'Como, sans-serif', fontWeight: 700, color: '#1E1E1E' }}>
                            Configuración de Linktree
                        </Typography>

                        {/* Imagen de fondo */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                Imagen de fondo
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => openImageSelection('bg')}
                                    sx={{
                                        borderColor: '#D8D8D8',
                                        color: '#1E1E1E',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        '&:hover': { borderColor: '#F72E41', backgroundColor: 'rgba(247,46,65,0.02)' },
                                    }}
                                >
                                    Seleccionar fondo
                                </Button>
                                {form.background_image_url && (
                                    <Box
                                        component="img"
                                        src={form.background_image_url}
                                        alt="fondo preview"
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            borderRadius: '12px',
                                            border: '2px solid #F0F0F0',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Logo */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                Logo
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon />}
                                    onClick={() => openImageSelection('logo')}
                                    sx={{
                                        borderColor: '#D8D8D8',
                                        color: '#1E1E1E',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        px: 3,
                                        py: 1,
                                        '&:hover': { borderColor: '#F72E41', backgroundColor: 'rgba(247,46,65,0.02)' },
                                    }}
                                >
                                    Seleccionar logo
                                </Button>
                                {form.logo_url && (
                                    <Box
                                        component="img"
                                        src={form.logo_url}
                                        alt="logo preview"
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            objectFit: 'cover',
                                            borderRadius: '12px',
                                            border: '2px solid #F0F0F0',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* Switch de Domicilios */}
                        <Box sx={{ mb: 4, bgcolor: '#FAFAFA', p: 3, borderRadius: '16px', border: '1px solid #EDEDED' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E1E1E' }}>
                                        Pedidos a Domicilio
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#666', mb: deliveryEnabled ? 1 : 0 }}>
                                        Habilita un botón especial en tu LinkTree para ventas a domicilio.
                                    </Typography>
                                    {deliveryEnabled && form.links.find(l => l.icon === 'delivery') && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#F72E41', fontWeight: 600 }}>
                                                URL Domicilios: {form.links.find(l => l.icon === 'delivery').url}
                                            </Typography>
                                            <Tooltip title="Copiar enlace de domicilios">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const url = form.links.find(l => l.icon === 'delivery').url;
                                                        navigator.clipboard.writeText(url);
                                                        setSnackbar({ open: true, message: 'Enlace de domicilios copiado', severity: 'success' });
                                                    }}
                                                    sx={{ color: '#F72E41', p: 0.5 }}
                                                >
                                                    <ContentCopyIcon fontSize="inherit" style={{ fontSize: '14px' }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    )}
                                </Box>
                                {isFetchingDeliveryUrl ? (
                                    <CircularProgress size={20} color="error" />
                                ) : (
                                    <Switch
                                        checked={deliveryEnabled}
                                        onChange={(e) => handleToggleDelivery(e.target.checked)}
                                        color="error"
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 4, borderColor: '#E0E0E0' }} />

                        {/* Lista de enlaces */}
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            Enlaces
                        </Typography>

                        <List sx={{ p: 0 }}>
                            {form.links.map((link, index) => {
                                const CurrentIcon = iconOptions.find(opt => opt.value === link.icon)?.icon || LinkIcon;
                                return (
                                    <ListItem
                                        key={link.id}
                                        disablePadding
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            bgcolor: '#FAFAFA',
                                            borderRadius: '16px',
                                            mb: 2,
                                            p: 2,
                                            gap: 2,
                                            border: '1px solid #EDEDED',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                                        }}
                                    >
                                        {/* Botones de ordenamiento */}
                                        <Stack direction="column" spacing={0.5}>
                                            <IconButton
                                                size="small"
                                                onClick={() => moveLinkUp(index)}
                                                disabled={index === 0}
                                                sx={{
                                                    bgcolor: index === 0 ? 'transparent' : '#F0F0F0',
                                                    '&:hover': { bgcolor: '#E0E0E0' },
                                                    width: 28,
                                                    height: 28,
                                                }}
                                            >
                                                <ArrowUpwardIcon fontSize="small" sx={{ color: index === 0 ? '#BDBDBD' : '#1E1E1E' }} />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => moveLinkDown(index)}
                                                disabled={index === form.links.length - 1}
                                                sx={{
                                                    bgcolor: index === form.links.length - 1 ? 'transparent' : '#F0F0F0',
                                                    '&:hover': { bgcolor: '#E0E0E0' },
                                                    width: 28,
                                                    height: 28,
                                                }}
                                            >
                                                <ArrowDownwardIcon fontSize="small" sx={{ color: index === form.links.length - 1 ? '#BDBDBD' : '#1E1E1E' }} />
                                            </IconButton>
                                        </Stack>

                                        {/* Selector de icono */}
                                        <Box>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    setIconMenuAnchor(e.currentTarget);
                                                    setIconMenuLinkId(link.id);
                                                }}
                                                sx={{ color: '#1E1E1E' }}
                                            >
                                                <CurrentIcon fontSize="small" />
                                            </IconButton>
                                            <Menu
                                                anchorEl={iconMenuAnchor}
                                                open={Boolean(iconMenuAnchor) && iconMenuLinkId === link.id}
                                                onClose={() => {
                                                    setIconMenuAnchor(null);
                                                    setIconMenuLinkId(null);
                                                }}
                                            >
                                                {iconOptions.map((opt) => (
                                                    <MenuItem
                                                        key={opt.value}
                                                        onClick={() => {
                                                            updateLink(link.id, 'icon', opt.value);
                                                            setIconMenuAnchor(null);
                                                            setIconMenuLinkId(null);
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <opt.icon fontSize="small" />
                                                            <Typography variant="body2">{opt.label}</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </Box>

                                        {/* Campos de texto */}
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Stack spacing={1.5}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Título (ej. Instagram)"
                                                    value={link.title}
                                                    onChange={(e) => updateLink(link.id, 'title', e.target.value)}
                                                    fullWidth
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '10px',
                                                            backgroundColor: 'white',
                                                        },
                                                    }}
                                                />
                                                <TextField
                                                    size="small"
                                                    placeholder="URL (https://...)"
                                                    value={link.url}
                                                    onChange={(e) => updateLink(link.id, 'url', e.target.value)}
                                                    fullWidth
                                                    disabled={link.icon === 'delivery'} // No permitir editar la URL nativa de domicilios
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '10px',
                                                            backgroundColor: link.icon === 'delivery' ? '#FAFAFA' : 'white',
                                                        },
                                                    }}
                                                />
                                            </Stack>
                                        </Box>

                                        {/* Acciones: Selector de color, Activo, Eliminar */}
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            {/* Círculo de previsualización de color con popover */}
                                            <Box
                                                onClick={(e) => openColorPicker(e, link.id)}
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    backgroundColor: link.button_color || '#FFFFFF',
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.2s',
                                                    '&:hover': { transform: 'scale(1.1)' },
                                                }}
                                            />
                                            <Popover
                                                open={colorPickerLinkId === link.id && Boolean(colorPickerAnchor)}
                                                anchorEl={colorPickerAnchor}
                                                onClose={closeColorPicker}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <Box sx={{ p: 2 }}>
                                                    <HexColorPicker
                                                        color={link.button_color || '#FFFFFF'}
                                                        onChange={(newColor) => updateLink(link.id, 'button_color', newColor)}
                                                    />
                                                </Box>
                                            </Popover>

                                            <Switch
                                                checked={link.is_active}
                                                onChange={(e) => updateLink(link.id, 'is_active', e.target.checked)}
                                                size="small"
                                                color="error"
                                            />
                                            <IconButton
                                                edge="end"
                                                onClick={() => removeLink(link.id)}
                                                sx={{ color: '#F72E41', '&:hover': { backgroundColor: 'rgba(247,46,65,0.04)' } }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Stack>
                                    </ListItem>
                                );
                            })}
                        </List>

                        {/* Bloque de URL pública (solo si existe public_id) */}
                        {data?.public_id && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    bgcolor: '#F5F5F5',
                                    borderRadius: '12px',
                                    p: 2,
                                    mt: 2,
                                    mb: 2,
                                    border: '1px solid #E0E0E0',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#1E1E1E',
                                        fontFamily: 'Como, sans-serif',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '70%',
                                    }}
                                >
                                    {`${baseUrl}/linktree/${data.public_id}`}
                                </Typography>
                                <Tooltip title="Copiar enlace">
                                    <IconButton size="small" onClick={handleCopyLink} sx={{ color: '#F72E41' }}>
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        <Button
                            startIcon={<AddIcon />}
                            onClick={addLink}
                            variant="outlined"
                            fullWidth
                            sx={{
                                mt: 2,
                                borderColor: '#D8D8D8',
                                color: '#1E1E1E',
                                borderRadius: '12px',
                                textTransform: 'none',
                                py: 1.5,
                                '&:hover': { borderColor: '#F72E41', backgroundColor: 'rgba(247,46,65,0.02)' },
                            }}
                        >
                            Agregar enlace
                        </Button>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                            <SendButton
                                onClick={handleSave}
                                disabled={loading}
                                sx={{
                                    px: 5,
                                    py: 1.5,
                                    borderRadius: '12px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                }}
                            >
                                {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Guardar cambios'}
                            </SendButton>
                        </Box>
                    </Paper>
                </Grid>

                {/* Columna derecha: Vista previa en teléfono (estilo público) */}
                <Grid item xs={12} md={6}>
                    <Box sx={{ position: 'sticky', top: 20 }}>
                        <Box
                            sx={{
                                minHeight: 600,
                                backgroundImage: form.background_image_url ? `url(${form.background_image_url})` : 'none',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundColor: '#f5f5f5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 2,
                                borderRadius: '32px',
                                border: '1px solid #E0E0E0',
                            }}
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    maxWidth: 340,
                                    width: '100%',
                                    borderRadius: '32px',
                                    overflow: 'hidden',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    backdropFilter: 'blur(10px)',
                                }}
                            >
                                <Box sx={{ p: 3 }}>
                                    {form.logo_url && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                            <Box
                                                component="img"
                                                src={form.logo_url}
                                                sx={{
                                                    width: 100,
                                                    height: 100,
                                                    borderRadius: '50%',
                                                    objectFit: 'cover',
                                                    border: '3px solid white',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 3 }}>
                                        {restaurant?.name || 'Mi Restaurante'}
                                    </Typography>

                                    <Stack spacing={2}>
                                        {form.links
                                            .filter((link) => link.is_active)
                                            .map((link) => {
                                                const IconComp = iconOptions.find(opt => opt.value === link.icon)?.icon || LinkIcon;
                                                const bgColor = link.button_color || (link.dark_mode ? '#1E1E1E' : '#FFFFFF');
                                                const textColor = getContrastText(bgColor);
                                                return (
                                                    <Button
                                                        key={link.id}
                                                        variant="contained"
                                                        fullWidth
                                                        startIcon={<IconComp />}
                                                        sx={{
                                                            bgcolor: bgColor,
                                                            color: textColor,
                                                            borderRadius: '40px',
                                                            textTransform: 'none',
                                                            py: 1.5,
                                                            fontSize: '1rem',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                            '&:hover': {
                                                                bgcolor: bgColor,
                                                                filter: 'brightness(0.95)',
                                                            },
                                                            justifyContent: 'flex-start',
                                                            pl: 3,
                                                        }}
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener"
                                                    >
                                                        {link.title || 'Enlace sin título'}
                                                    </Button>
                                                );
                                            })}
                                    </Stack>
                                </Box>
                            </Paper>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Modal Alerta WhatsApp Ausente */}
            <Dialog
                open={whatsappAlertOpen}
                onClose={() => setWhatsappAlertOpen(false)}
                PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#1E1E1E' }}>
                    Requiere configuración previa
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#666', mb: 2 }}>
                        Para habilitar los Pedidos a Domicilio, <strong>tu restaurante debe tener un número de WhatsApp asociado.</strong> 
                        Allí es donde enviaremos las órdenes de tus clientes.
                    </DialogContentText>
                    <DialogContentText sx={{ color: '#1E1E1E', fontWeight: 600 }}>
                        Por favor dirígete a la sección de "Ajustes", configura tu número telefónico y luego regresa a encender este interruptor.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setWhatsappAlertOpen(false)} 
                        variant="contained"
                        sx={{ 
                            textTransform: 'none', 
                            fontWeight: 700, 
                            borderRadius: '8px',
                            backgroundColor: '#8266ff',
                            '&:hover': { backgroundColor: '#6e54e0' }
                        }}
                    >
                        Entendido
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de selección de imágenes */}
            <BackgroundImageSelectionDialog
                open={imageSelectionOpen}
                handleClose={handleCloseImageSelection}
                restaurantId={restaurantId}
                onSelectImage={handleImageSelected}
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px', fontWeight: 500 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}